import { forwardRef } from "react";
import selegoLogo from "@/assets/selego.jpeg";

const PAGE_WIDTH = 794;
const PAGE_HEIGHT = 1122;
const TABLE_HEADER_HEIGHT = 32;
const TABLE_ITEM_TITLE_HEIGHT = 36;
const TABLE_ITEM_DESC_HEIGHT = 72;
const SUMMARY_HEADER_HEIGHT = 32;
const SUMMARY_ITEM_HEIGHT = 40;
const ENTITIES_HEIGHT = 256;
const INFOS_HEIGHT = 100;
const SPACING = 32;
const DETAILS_HEIGHT = 32;
const TABLE_FOOTER_HEIGHT = 48;

const InvoicePdf = forwardRef(({ invoice, project, organisation }, ref) => {
  if (!invoice || !project || !organisation) return null;

  // Group items by name (same logic as pdf app)
  const items = {};
  (invoice.itemsInvoice || []).forEach((item) => {
    if (items[item._id]) items[item._id].push(item);
    else items[item._id] = [item];
  });

  // Heights of each item
  const heights = [];
  Object.values(items).forEach((item) => {
    heights.push(TABLE_ITEM_TITLE_HEIGHT + item.length * TABLE_ITEM_DESC_HEIGHT);
  });

  // Paginate items
  const pageItems = [[]];
  let currentPage = 0;
  let currentPagePosition = ENTITIES_HEIGHT + SPACING + INFOS_HEIGHT + SPACING + TABLE_HEADER_HEIGHT;
  Object.entries(items).forEach(([, item], index) => {
    const itemHeight = heights[index];
    if (currentPagePosition + itemHeight < PAGE_HEIGHT - TABLE_FOOTER_HEIGHT) {
      pageItems[currentPage].push(item);
      currentPagePosition += itemHeight;
    } else {
      currentPage++;
      pageItems[currentPage] = [item];
      currentPagePosition = itemHeight + TABLE_HEADER_HEIGHT;
    }
  });

  let summaryHeight = SUMMARY_HEADER_HEIGHT * 2 + SPACING + DETAILS_HEIGHT;
  if (invoice.discount > 0 || invoice.taxValue > 0) summaryHeight += SUMMARY_ITEM_HEIGHT;
  if (invoice.discount > 0) summaryHeight += SUMMARY_ITEM_HEIGHT * 2;
  if (invoice.taxValue > 0) summaryHeight += SUMMARY_ITEM_HEIGHT;

  const termsText =
    invoice.termsAndConditions ||
    "Please pay within 30 days. In the event of late payment, a penalty of 15% interest rate will be applied, to which will be added a lump sum compensation for recovery costs of €40.";

  const showSummaryOnPage = (pageIndex, totalPages) => {
    if (totalPages === 1 && pageIndex === 0) return PAGE_HEIGHT - (currentPagePosition + TABLE_FOOTER_HEIGHT) > summaryHeight;
    if (pageIndex === totalPages - 1) return PAGE_HEIGHT - (currentPagePosition + TABLE_FOOTER_HEIGHT) > summaryHeight;
    return false;
  };

  const needsExtraSummaryPage = !showSummaryOnPage(pageItems.length - 1, pageItems.length);

  return (
    <div ref={ref} style={{ background: "white" }}>
      {/* Page 1 */}
      <section className="flex flex-col px-12 py-16 space-y-8" style={{ height: PAGE_HEIGHT, width: PAGE_WIDTH }}>
        <div className="flex flex-col justify-between" style={{ height: ENTITIES_HEIGHT }}>
          <div className="flex items-start justify-between">
            <img src={selegoLogo} alt="logo" className="w-12 h-12" style={{ width: 48, height: 48 }} />
            <p className="text-xl font-semibold">Invoice #{invoice.index}</p>
          </div>
          <Entities invoice={invoice} project={project} organisation={organisation} />
        </div>
        <hr />
        <div className="flex flex-col justify-between" style={{ height: INFOS_HEIGHT }}>
          <Infos invoice={invoice} />
        </div>
        <TablePdf items={pageItems[0]} invoice={pageItems.length === 1 ? invoice : null} />
        {pageItems.length === 1 && showSummaryOnPage(0, 1) && <SummaryBlock invoice={invoice} termsText={termsText} />}
      </section>

      {/* Extra pages for items */}
      {pageItems.length > 1 &&
        pageItems.slice(1).map((items, index) => (
          <section key={index} className="flex flex-col px-12 py-16 space-y-8" style={{ height: PAGE_HEIGHT, width: PAGE_WIDTH, pageBreakBefore: "always" }}>
            <TablePdf items={items} invoice={index === pageItems.length - 2 ? invoice : null} />
            {index === pageItems.length - 2 && showSummaryOnPage(index + 1, pageItems.length) && <SummaryBlock invoice={invoice} termsText={termsText} />}
          </section>
        ))}

      {/* Extra summary page if it doesn't fit */}
      {needsExtraSummaryPage && (
        <section className="flex flex-col px-12 py-16 space-y-8" style={{ height: PAGE_HEIGHT, width: PAGE_WIDTH, pageBreakBefore: "always" }}>
          <SummaryBlock invoice={invoice} termsText={termsText} />
        </section>
      )}
    </div>
  );
});

const SummaryBlock = ({ invoice, termsText }) => (
  <div className="flex-1 flex flex-col justify-between gap-8">
    <div className="flex items-start justify-end">
      <div className="w-1/2">
        <Summary invoice={invoice} />
      </div>
    </div>
    <p className="text-center text-xs" style={{ height: DETAILS_HEIGHT }}>
      {termsText}
    </p>
  </div>
);

const Entities = ({ invoice, project, organisation }) => (
  <div className="flex items-start gap-4">
    <div className="flex-1">
      <div className="w-full flex flex-col">
        <div className="mb-2">
          <p className="text-base">Seller</p>
          <h2 className="text-lg font-semibold">{organisation.name}</h2>
        </div>
        <p className="text-sm">{organisation.address}</p>
        <p>
          <span className="text-sm font-semibold mr-2">Email:</span>
          {organisation.email}
          <br />
          <span className="text-sm font-semibold mr-2">VAT:</span>
          {organisation.vat} <br />
          <span className="text-sm font-semibold mr-2">IBAN:</span>
          {organisation.iban} <br />
        </p>
      </div>
    </div>
    <div className="flex-1">
      <div className="w-full flex flex-col">
        <div className="mb-2">
          <p className="text-base">Customer</p>
          <h2 className="text-lg font-semibold">{invoice.customer_name || project.clientName}</h2>
        </div>
        {(invoice.customer_address || project.clientAddress) && <p className="text-sm">{invoice.customer_address || project.clientAddress}</p>}
        <p>
          <span className="text-sm font-semibold mr-2">Email:</span>
          {(invoice.customer_emails || project.clientMails || []).join(", ")} <br />
          <span className="text-sm font-semibold mr-2">VAT:</span> {invoice.customer_vat || project.clientVat}
          <br />
          <span className="text-sm font-semibold mr-2">SIREN:</span>
          {invoice.customer_siren || project.clientSiret} <br />
        </p>
      </div>
    </div>
  </div>
);

const Infos = ({ invoice }) => (
  <div className="flex gap-x-10 text-sm">
    <div>
      <label className="font-semibold text-right">Invoice name:</label>
      <p>{invoice.name}</p>
      <label className="font-semibold text-right">Issued date:</label>
      <p>{invoice.sentAt ? new Date(invoice.sentAt).toLocaleDateString("fr-FR") : "-"}</p>
    </div>
    <div>
      <label className="font-semibold text-right">Invoice number:</label>
      <p>{invoice.index}</p>
      <label className="font-semibold text-right">Due date:</label>
      <p>{invoice.dueAt ? new Date(invoice.dueAt).toLocaleDateString("fr-FR") : "-"}</p>
    </div>
  </div>
);

const TablePdf = ({ items, invoice }) => (
  <div className="w-full flex flex-col">
    <ul>
      <li className="bg-blue-200 flex rounded-t-lg items-center px-4" style={{ height: TABLE_HEADER_HEIGHT }}>
        <h2 className="flex-1 text-sm font-medium">Name</h2>
        <h2 className="w-[15%] text-sm font-medium text-right">Quantity</h2>
        <h2 className="w-[15%] text-sm font-medium text-right">Price</h2>
        <h2 className="w-[15%] text-sm font-medium text-right">Total</h2>
      </li>
      {items.map((item, index) => (
        <li key={index} className={`px-3 text-sm ${index % 2 === 0 ? "bg-gray-100" : "bg-gray-50"}`}>
          <div className="flex items-center" style={{ height: TABLE_ITEM_TITLE_HEIGHT }}>
            <p className="flex-1">{item[0].name}</p>
            <p className="w-[15%] text-right">
              {item[0].quantity} {item[0].unit}
            </p>
            <p className="w-[15%] text-right">
              {(item[0].price || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}
            </p>
            <p className="w-[15%] text-right">
              {(item[0].totalPrice || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}
            </p>
          </div>
          {item.map((i, idx) => (
            <div key={idx} className="flex items-center text-sm pb-2">
              <div className="flex-1 w-1/4 px-[15%]">
                <p>{i.description}</p>
              </div>
            </div>
          ))}
        </li>
      ))}
      {invoice && (
        <li className="bg-blue-200 flex rounded-b-lg items-center px-4 font-semibold" style={{ height: TABLE_HEADER_HEIGHT }}>
          <h2 className="flex-1 text-sm font-medium">Total</h2>
          <h2 className="w-[12%] text-right text-sm font-medium">
            {(invoice.subTotal || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}
          </h2>
        </li>
      )}
    </ul>
  </div>
);

const Summary = ({ invoice }) => (
  <div className="w-full flex flex-col text-sm">
    <ul>
      <li className="bg-blue-200 flex rounded-t-lg items-center px-4 font-semibold" style={{ height: SUMMARY_HEADER_HEIGHT }}>
        <h2 className="flex-1 text-sm font-medium">Invoice Summary</h2>
      </li>
      {invoice.discount > 0 && (
        <>
          <li className="flex px-4 items-center bg-gray-50" style={{ height: SUMMARY_ITEM_HEIGHT }}>
            <p className="flex-1">Total</p>
            <p className="w-1/3 text-right">{(invoice.subTotal || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}</p>
          </li>
          <li className="flex px-4 items-center bg-gray-50" style={{ height: SUMMARY_ITEM_HEIGHT }}>
            <p className="flex-1">Discount {(invoice.discount / 100 || 0).toLocaleString("fr", { style: "percent" })}</p>
            <p className="w-1/3 text-right">
              -{(invoice.subTotal - (invoice.subTotal * (100 - invoice.discount)) / 100).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}
            </p>
          </li>
        </>
      )}
      <li className="flex px-4 items-center bg-gray-50" style={{ height: SUMMARY_ITEM_HEIGHT }}>
        <p className="flex-1">Untaxed total</p>
        <p className="w-1/3 text-right">
          {(invoice.subTotal - (invoice.subTotal - (invoice.subTotal * (100 - invoice.discount)) / 100)).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}
        </p>
      </li>
      <li className="flex px-4 items-center bg-gray-50" style={{ height: SUMMARY_ITEM_HEIGHT }}>
        <div className="flex-1 text-right">
          <p>Tax {invoice.taxType || "(not applicable)"}</p>
          {(!invoice.taxType || invoice.taxType === "N/A (Not applicable)") && (
            <span className="text-xs italic text-gray-600">VAT reverse charge – Article 194 of Directive 2006/112/EC</span>
          )}
        </div>
        <p className="w-1/3 text-right">{(invoice.taxValue || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}</p>
      </li>
      <li className="bg-blue-200 flex rounded-b-lg items-center px-4" style={{ height: SUMMARY_HEADER_HEIGHT }}>
        <h2 className="flex-1 text-sm font-medium">Total due</h2>
        <h2 className="w-1/3 text-right text-sm font-medium">{(invoice.total || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}</h2>
      </li>
    </ul>
  </div>
);

export default InvoicePdf;
